const MISSING_DESTINATION_MESSAGE =
    "A requested file or directory could not be found at the time an operation was processed.";
const ARRAY_BUFFER_ALLOCATION_MESSAGE = "Array buffer allocation failed";

function getErrorName(error: unknown) {
    return error && typeof error === "object" && "name" in error ? error.name : undefined;
}

export class PlaylistFetchError extends Error {
    constructor(message = "Failed to fetch the video playlist", cause?: unknown) {
        super(message, {cause});
        this.name = "PlaylistFetchError";
    }
}

export class StreamSaverError extends Error {
    constructor(message = "The browser download stream could not start", cause?: unknown) {
        super(message, {cause});
        this.name = "StreamSaverError";
    }
}

export function isMissingDownloadDestinationError(error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    return getErrorName(error) === "NotFoundError" || errorMessage.includes(MISSING_DESTINATION_MESSAGE);
}

export function isArrayBufferAllocationError(error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    return errorMessage.toLowerCase().includes(ARRAY_BUFFER_ALLOCATION_MESSAGE.toLowerCase());
}

export type Mp3ConversionStage =
    | "read-stream"
    | "load-ffmpeg"
    | "write-input"
    | "convert"
    | "read-output";

const MP3_CONVERSION_ERROR_MARKER = "Mp3ConversionError";

export class Mp3ConversionError extends Error {
    readonly name = MP3_CONVERSION_ERROR_MARKER;
    readonly stage: Mp3ConversionStage;
    readonly exitCode?: number;
    readonly logTail: string[];

    constructor(
        stage: Mp3ConversionStage,
        message: string,
        options: {cause?: unknown; exitCode?: number; logTail?: string[]} = {}
    ) {
        super(message);
        // `target: es5` breaks the prototype chain when extending built-ins.
        Object.setPrototypeOf(this, Mp3ConversionError.prototype);
        this.stage = stage;
        this.exitCode = options.exitCode;
        this.logTail = options.logTail ?? [];
        if (options.cause !== undefined) {
            (this as {cause?: unknown}).cause = options.cause;
        }
    }
}

export function isMp3ConversionError(error: unknown): error is Mp3ConversionError {
    return (
        error instanceof Mp3ConversionError ||
        (!!error && typeof error === "object" && (error as Error).name === MP3_CONVERSION_ERROR_MARKER)
    );
}

export function isWriteAbortedError(error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    return getErrorName(error) === "AbortError" || errorMessage.toLowerCase().includes("abort");
}

export function isPlaylistFetchError(error: unknown) {
    return error instanceof PlaylistFetchError || getErrorName(error) === "PlaylistFetchError";
}

export function isStreamSaverError(error: unknown) {
    return error instanceof StreamSaverError || getErrorName(error) === "StreamSaverError";
}
